import { useEffect, useState } from "react";
import { FloppyDisk, Buildings, MapPin, Plus, PencilSimple, Trash, Lock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import Modal, { Field, inputCls, PrimaryBtn, GhostBtn } from "@/components/Modal";
import { toast } from "sonner";

const emptyLoc = { name: "", address: "", phone: "", active: true };

export default function Settings() {
  const { refresh } = useAuth();
  const { t, lang } = useI18n();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [locations, setLocations] = useState([]);
  const [isElite, setIsElite] = useState(false);
  const [locDialog, setLocDialog] = useState({ open: false, id: null });
  const [locForm, setLocForm] = useState(emptyLoc);
  const [locSaving, setLocSaving] = useState(false);

  const loadLocations = () => api.get("/locations").then(({ data }) => setLocations(data));

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm(data));
    api.get("/billing/me").then(({ data }) => setIsElite(data.effective_plan === "elite"));
    loadLocations();
  }, []);

  if (!form) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-[#E5E2DC] border-t-[#2C3E35] animate-spin" /></div>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        business_name: form.name, business_type: form.type, currency: form.currency,
        open_hour: Number(form.open_hour), close_hour: Number(form.close_hour),
        phone: form.phone, address: form.address,
      };
      const { data } = await api.put("/settings", payload);
      setForm(data);
      await refresh();
      toast.success(t.settings.saved);
    } catch (e) { toast.error(t.dlg.failed); } finally { setSaving(false); }
  };

  const openNewLoc = () => { setLocForm(emptyLoc); setLocDialog({ open: true, id: null }); };
  const openEditLoc = (l) => { setLocForm({ name: l.name, address: l.address || "", phone: l.phone || "", active: l.active !== false }); setLocDialog({ open: true, id: l.id }); };
  const setLoc = (k) => (e) => setLocForm({ ...locForm, [k]: e.target.value });

  const saveLoc = async () => {
    if (!locForm.name.trim()) { toast.error(t.cust.nameReq); return; }
    setLocSaving(true);
    try {
      if (locDialog.id) await api.put(`/locations/${locDialog.id}`, locForm);
      else await api.post("/locations", locForm);
      toast.success(locDialog.id ? t.loc.updated : t.loc.added);
      setLocDialog({ open: false, id: null });
      loadLocations();
    } catch (e) { toast.error(t.dlg.failed); } finally { setLocSaving(false); }
  };

  const delLoc = async (id) => { await api.delete(`/locations/${id}`); toast.success(t.loc.removed); loadLocations(); };

  return (
    <div className="fade-up space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <p className="label-caps">{t.settings.overline}</p>
        <h1 className="font-heading text-3xl font-light tracking-tight mt-1">{t.settings.title}</h1>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2DC] p-6 space-y-5">
        <div className="flex items-center gap-2 pb-2"><Buildings size={20} weight="duotone" color="#2C3E35" /><span className="font-heading font-medium">{t.settings.profile}</span></div>
        <Field label={t.settings.fName}><input data-testid="settings-name" className={inputCls} value={form.name || ""} onChange={set("name")} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t.settings.fType}>
            <select data-testid="settings-type" className={inputCls} value={form.type || "salon"} onChange={set("type")}>
              <option value="salon">{lang === "en" ? "Beauty Salon" : "Salón de Belleza"}</option>
              <option value="barbershop">{lang === "en" ? "Barbershop" : "Barbería"}</option>
              <option value="spa">Spa</option>
              <option value="nails">{lang === "en" ? "Nail Studio" : "Estudio de Uñas"}</option>
            </select>
          </Field>
          <Field label={t.settings.fCurrency}>
            <select data-testid="settings-currency" className={inputCls} value={form.currency || "USD"} onChange={set("currency")}>
              <option value="CRC">CRC ₡</option>
              <option value="USD">USD $</option>
              <option value="EUR">EUR €</option>
              <option value="MXN">MXN $</option>
              <option value="BRL">BRL R$</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t.settings.fOpen}><input data-testid="settings-open" type="number" min={0} max={23} className={inputCls} value={form.open_hour ?? 9} onChange={set("open_hour")} /></Field>
          <Field label={t.settings.fClose}><input data-testid="settings-close" type="number" min={1} max={24} className={inputCls} value={form.close_hour ?? 19} onChange={set("close_hour")} /></Field>
        </div>
        <Field label={t.settings.fPhone}><input data-testid="settings-phone" className={inputCls} value={form.phone || ""} onChange={set("phone")} /></Field>
        <Field label={t.settings.fAddress}><input data-testid="settings-address" className={inputCls} value={form.address || ""} onChange={set("address")} /></Field>
        <div className="pt-2">
          <PrimaryBtn onClick={save} disabled={saving} data-testid="settings-save"><FloppyDisk size={16} /> {saving ? t.btn.saving : t.settings.save}</PrimaryBtn>
        </div>
      </div>

      {/* Multi-location (Elite) */}
      <div className="bg-white rounded-xl border border-[#E5E2DC] p-6 space-y-4" data-testid="locations-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin size={20} weight="duotone" color="#2C3E35" />
            <span className="font-heading font-medium">{t.loc.title}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#9a7d1e]">Elite</span>
          </div>
          {isElite && (
            <button data-testid="new-location-btn" onClick={openNewLoc}
              className="px-3 py-2 rounded-lg bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#1E2B25] transition-colors inline-flex items-center gap-2">
              <Plus size={15} weight="bold" /> {t.loc.add}
            </button>
          )}
        </div>

        {!isElite ? (
          <div className="rounded-lg bg-[#F5F2ED] border border-[#E5E2DC] p-5 flex items-start gap-3" data-testid="locations-elite-gate">
            <Lock size={18} className="text-[#9a7d1e] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-[#3a3a38]">{t.loc.eliteOnly}</p>
              <Link to="/dashboard/billing" data-testid="locations-upgrade-link" className="mt-2 inline-block text-sm font-semibold text-[#B86B5A] hover:underline">{t.loc.upgrade} →</Link>
            </div>
          </div>
        ) : locations.length === 0 ? (
          <p className="text-sm text-[#6B6A67]">{t.loc.none}</p>
        ) : (
          <div className="space-y-2">
            {locations.map((l) => (
              <div key={l.id} data-testid={`location-row-${l.id}`} className="flex items-center justify-between rounded-lg border border-[#E5E2DC] px-4 py-3 group">
                <div>
                  <div className="font-semibold text-sm">{l.name}</div>
                  <div className="text-xs text-[#6B6A67]">{[l.address, l.phone].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`edit-location-${l.id}`} onClick={() => openEditLoc(l)} className="p-1.5 rounded-lg text-[#6B6A67] hover:bg-[#E8E4DF]"><PencilSimple size={15} /></button>
                  <button data-testid={`del-location-${l.id}`} onClick={() => delLoc(l.id)} className="p-1.5 rounded-lg text-[#6B6A67] hover:bg-[#B86B5A]/12 hover:text-[#B86B5A]"><Trash size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
        <p className="label-caps mb-2">{t.settings.comingTitle}</p>
        <p className="text-sm text-[#6B6A67]">{t.settings.comingBody}</p>
      </div>

      <Modal open={locDialog.open} onClose={() => setLocDialog({ open: false, id: null })} testid="location-dialog"
        title={locDialog.id ? t.loc.edit : t.loc.add}
        footer={<><GhostBtn onClick={() => setLocDialog({ open: false, id: null })}>{t.btn.cancel}</GhostBtn><PrimaryBtn onClick={saveLoc} disabled={locSaving} data-testid="location-save">{locSaving ? t.btn.saving : t.btn.save}</PrimaryBtn></>}>
        <div className="space-y-4">
          <Field label={t.loc.fName}><input data-testid="location-name" className={inputCls} value={locForm.name} onChange={setLoc("name")} placeholder="Downtown Studio" /></Field>
          <Field label={t.loc.fAddress}><input data-testid="location-address" className={inputCls} value={locForm.address} onChange={setLoc("address")} /></Field>
          <Field label={t.loc.fPhone}><input data-testid="location-phone" className={inputCls} value={locForm.phone} onChange={setLoc("phone")} /></Field>
        </div>
      </Modal>
    </div>
  );
}
